import { useCurrentOrganizationId } from 'hooks/use-current-organization-id';
import useApiClient from 'hooks/use-api-client';
import { useQuery, useMutation } from '@tanstack/react-query';
import isString from 'lodash/isString';
import isEmpty from 'lodash/isEmpty';
import type { Blueprint, CreateBlueprint, UpdateBlueprint } from 'types/api.types';
import { getQueryClientInstance } from 'stores/rq/common/query-client-provider';
import { filterOutById } from 'stores/rq/common/react-query-utils';
import uniq from 'lodash/uniq';
import flow from 'lodash/fp/flow';
import orderBy from 'lodash/fp/orderBy';

const assignableParamsFromBlueprint = (blueprint?: Pick<Blueprint, 'creationScope' | 'creationScopeId'>) => ({
  projectId: blueprint?.creationScope === 'PROJECT' ? blueprint.creationScopeId : undefined
});

const useCacheKeys = () => {
  const orgId = useCurrentOrganizationId();
  const baseKey = [orgId, 'blueprints'] as const;

  return {
    single: (id: string) => [...baseKey, 'single', { id }] as const,
    byProjectId: (projectId?: string) => [...baseKey, 'projectBlueprints', { projectId }] as const,
    assignable: (
      params: { projectId?: string; blueprint?: Pick<Blueprint, 'creationScope' | 'creationScopeId'> } = {}
    ) => {
      const resolvedProjectId = params.blueprint
        ? assignableParamsFromBlueprint(params.blueprint).projectId
        : params.projectId;
      return [...baseKey, 'assignableBlueprints', { projectId: resolvedProjectId }] as const;
    }
  } as const;
};

const useCommonHookSetup = () => {
  return {
    apiClient: useApiClient(),
    queryClient: getQueryClientInstance(),
    organizationId: useCurrentOrganizationId(),
    ...useCacheKeys()
  } as const;
};

export interface UseGetBlueprintsOptions {
  enabled?: boolean;
  projectId?: string;
}

export const useGetBlueprints = ({ enabled = true, projectId }: UseGetBlueprintsOptions = {}) => {
  const { apiClient, organizationId } = useCommonHookSetup();
  const { assignable } = useCacheKeys();

  // eslint-disable-next-line @tanstack/query/exhaustive-deps
  return useQuery<Blueprint[] | undefined, Error>({
    queryKey: assignable({ projectId }),
    queryFn: async () => {
      if (!!projectId) {
        const data = await apiClient.blueprints.getAssignableForProject(projectId);
        return sortBlueprints(data);
      }
      const data = await apiClient.blueprints.getForOrganization(organizationId);
      return sortBlueprints(data);
    },
    enabled: isString(projectId) ? enabled : isString(organizationId) && enabled
  });
};

export const useCreateBlueprint = () => {
  const { apiClient, queryClient, assignable, single, byProjectId } = useCommonHookSetup();
  return useMutation({
    mutationFn: (blueprint: CreateBlueprint) => apiClient.blueprints.create(blueprint),
    onSuccess: (newBlueprint: Blueprint) => {
      queryClient.setQueryData(single(newBlueprint.id), newBlueprint);
      queryClient.refetchQueries({ queryKey: assignable() });
      newBlueprint?.projectIds?.forEach(projectId => {
        queryClient.refetchQueries({ queryKey: byProjectId(projectId) });
        queryClient.refetchQueries({ queryKey: assignable({ projectId }) });
      });
    }
  });
};

export const useUpdateBlueprint = () => {
  const { apiClient, queryClient, assignable, single, byProjectId } = useCommonHookSetup();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateBlueprint }) => apiClient.blueprints.update(id, data),
    onSuccess: (updatedBlueprint: Blueprint) => {
      queryClient.setQueryData(single(updatedBlueprint.id), updatedBlueprint);
    },
    onError: (_error, { id }) => {
      queryClient.refetchQueries({ queryKey: single(id) });
    },
    onSettled: updatedBlueprint => {
      queryClient.refetchQueries({ queryKey: assignable({ blueprint: updatedBlueprint }) });
      updatedBlueprint?.projectIds?.forEach(projectId =>
        queryClient.refetchQueries({ queryKey: byProjectId(projectId) })
      );
    }
  });
};

export const useRemoveBlueprint = () => {
  const { apiClient, queryClient, assignable, single, byProjectId } = useCommonHookSetup();

  return useMutation({
    mutationFn: (id: string) => apiClient.blueprints.remove(id),
    onMutate: (id: string) => {
      const previousAllBlueprints: Blueprint[] | undefined = queryClient.getQueryData(assignable());
      const previousSingleBlueprint: Blueprint[] | undefined = queryClient.getQueryData(single(id));
      queryClient.setQueryData(assignable(), filterOutById<Blueprint>(id));
      queryClient.removeQueries({ queryKey: single(id) });
      return { previousAllBlueprints, previousSingleBlueprint };
    },
    onError: (_error, id, mutateContext) => {
      queryClient.setQueryData(assignable(), mutateContext?.previousAllBlueprints);
      queryClient.setQueryData(single(id), mutateContext?.previousSingleBlueprint);
    },
    onSettled: () => {
      queryClient.refetchQueries({ queryKey: byProjectId() });
      queryClient.refetchQueries({ queryKey: assignable() });
    }
  });
};

export const useRemoveBlueprintsFromProject = () => {
  const { apiClient, queryClient, assignable, byProjectId, single } = useCommonHookSetup();

  return useMutation({
    mutationFn: ({ projectId, blueprintIds }: { projectId: string; blueprintIds: string[] }) =>
      Promise.all(
        blueprintIds.map(blueprintId => apiClient.blueprints.removeBlueprintFromProject(projectId, blueprintId))
      ).catch(err => {
        if (err?.response?.status === 404) {
          return [];
        } else {
          throw err;
        }
      }),
    onSettled: (_, _err, { projectId, blueprintIds }) => {
      blueprintIds.forEach(blueprintId => queryClient.refetchQueries({ queryKey: single(blueprintId) }));
      queryClient.refetchQueries({ queryKey: byProjectId(projectId) });
      queryClient.refetchQueries({ queryKey: assignable({ projectId }) });
      queryClient.refetchQueries({ queryKey: assignable() });
    }
  });
};

export const useAddBlueprintsToProject = () => {
  const { apiClient, queryClient, assignable, single, byProjectId } = useCommonHookSetup();

  return useMutation({
    mutationFn: ({ projectId, blueprintIds }: { projectId: string; blueprintIds: string[] }) =>
      Promise.all(blueprintIds.map(blueprintId => apiClient.blueprints.addBlueprintToProject(projectId, blueprintId))),
    onSettled: (_, _err, { projectId, blueprintIds }) => {
      blueprintIds.forEach(blueprintId => queryClient.refetchQueries({ queryKey: single(blueprintId) }));
      queryClient.refetchQueries({ queryKey: byProjectId(projectId) });
      queryClient.refetchQueries({ queryKey: assignable({ projectId }) });
      queryClient.refetchQueries({ queryKey: assignable() });
    }
  });
};

export const useGetBlueprintsByProjectId = (projectId: string) => {
  const { apiClient, queryClient, assignable, byProjectId } = useCommonHookSetup();

  // for fetch by project id we return only the projectId value and not all the template projectIds like other requests so we set the value from existing cache
  const setBlueprintProjectIdsFromExistingCache = (allBlueprints?: Blueprint[]) => {
    return (blueprint: Blueprint) => {
      if (blueprint.isAllProjects) {
        return { ...blueprint, projectIds: [] };
      }
      const existingBlueprintProjects =
        allBlueprints?.find(storedBlueprint => storedBlueprint.id === blueprint.id)?.projectIds || [];
      return {
        ...blueprint,
        projectIds: uniq([blueprint.projectId!, ...existingBlueprintProjects].flat())
      };
    };
  };
  const { data: allBlueprints } = useGetBlueprints();
  // eslint-disable-next-line @tanstack/query/exhaustive-deps
  return useQuery<Blueprint[], any, Blueprint[], ReturnType<typeof byProjectId>>({
    queryKey: byProjectId(projectId),
    enabled: !isEmpty(allBlueprints),
    queryFn: async ({ queryKey: [, , , { projectId }] }) => {
      const blueprintsForProject: Blueprint[] = await apiClient.blueprints.getForProject(projectId!);
      return sortBlueprints(blueprintsForProject.map(setBlueprintProjectIdsFromExistingCache(allBlueprints)));
    },
    placeholderData: () => {
      const allBlueprints = queryClient.getQueryData<Blueprint[]>(assignable());
      return sortBlueprints(
        allBlueprints?.filter(blueprint => blueprint.isAllProjects || blueprint.projectIds?.includes(projectId))
      );
    }
  });
};

export const useGetBlueprint = (blueprintId: string | null | undefined) => {
  const { apiClient, queryClient, assignable, single } = useCommonHookSetup();

  return useQuery<Blueprint, any, Blueprint, ReturnType<typeof single>>({
    queryKey: single(blueprintId!),
    queryFn: ({ queryKey: [, , , { id }] }) => apiClient.blueprints.getById(id),
    enabled: isString(blueprintId),
    placeholderData: () => {
      const allAssignable = queryClient.getQueryData<Blueprint[]>(assignable());
      return allAssignable?.find(blueprint => blueprint.id === blueprintId);
    },
    refetchOnWindowFocus: false
  });
};

function sortBlueprints(blueprints: Blueprint[]): Blueprint[];
function sortBlueprints(blueprints?: Blueprint[]): undefined | Blueprint[];
function sortBlueprints(blueprints?: Blueprint[]) {
  if (!blueprints) {
    return undefined;
  }
  return flow(orderBy('createdAt', 'desc'))(blueprints) as Blueprint[];
}
